import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

interface UploadMediaParams {
  files: File[];
  name?: string;
  description?: string;
  altText?: string;
  tags?: string[];
}

// Media upload mutation
export const useUploadMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      files,
      name,
      description,
      altText,
      tags,
    }: UploadMediaParams) => {
      const formData = new FormData();
      if (name) formData.append("name", name);

      // Add all files
      files.forEach((file) => {
        formData.append("files", file);
      });

      // Add optional fields if provided
      if (description) {
        formData.append("description", description);
      }

      if (altText) {
        formData.append("alt_text", altText);
      }

      if (tags && tags.length > 0) {
        formData.append("tags", tags.join(","));
      }

      const response = await api.post("/api/marketing/media/upload/", formData);

      return response.data;
    },
    onSuccess: () => {
      // Invalidate the combined media query to refresh the media list
      queryClient.invalidateQueries({ queryKey: ["combined-media"] });
    },
  });
};

// Other marketing-related hooks can be added here as needed
export const useMediaLibrary = (
  searchQuery?: string,
  selectedTags?: string[],
) => {
  return useQuery({
    queryKey: ["combined-media", searchQuery, selectedTags],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      selectedTags?.forEach((tag) => params.append("tags", tag));

      const response = await api.get(
        `/api/marketing/combined-media/?${params.toString()}`,
      );
      return response.data || [];
    },
  });
};

export const useDeleteMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaId: string | number) => {
      // Extract the actual ID from combined ID format
      const idStr = mediaId.toString();
      if (idStr.startsWith("marketing_")) {
        const actualId = idStr.replace("marketing_", "");
        return api.delete(`/api/marketing/media/${actualId}/`);
      } else {
        throw new Error("Can only delete marketing media files");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["combined-media"] });
    },
  });
};

export const useUpdateMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string | number;
      name: string;
      description?: string;
      alt_text?: string;
      tags?: string[];
    }) => {
      const idStr = id.toString();

      // Handle production photos differently
      if (idStr.startsWith("production_")) {
        const actualId = idStr.replace("production_", "");

        // Production photos API uses different field names
        // Only send fields that production photos support
        const productionData: any = {};

        // Production photos use 'description' field
        if (data.description !== undefined) {
          productionData.description = data.description;
        }

        // Production photos use 'tag_names' as comma-separated string
        if (data.tags) {
          productionData.tag_names = data.tags.join(", ");
        }

        // Production photos don't have 'name' or 'alt_text' fields
        // So we don't include those in the update

        return api.patch(`/api/photos/${actualId}/`, productionData);
      } else {
        // Marketing media - extract ID if needed and use existing logic
        const actualId = idStr.startsWith("marketing_")
          ? idStr.replace("marketing_", "")
          : idStr;

        return api.patch(`/api/marketing/media/${actualId}/`, {
          ...data,
          ...(data.tags !== undefined ? { tags: data.tags } : {}),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["combined-media"] });
    },
  });
};
